import * as React from "react";
import { useTranslation } from "react-i18next";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import frLocale from "i18n-iso-countries/langs/fr.json";
import deLocale from "i18n-iso-countries/langs/de.json";
import { hasFlag } from "country-flag-icons";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

countries.registerLocale(enLocale);
countries.registerLocale(frLocale);
countries.registerLocale(deLocale);

/** SVG flag URL from country-flag-icons package (served by Vite from node_modules) */
function flagUrl(code: string): string {
  return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`;
}

/** Small inline flag image */
function FlagIcon({ code, className }: { code: string; className?: string }) {
  if (!hasFlag(code)) return null;
  return (
    <img
      src={flagUrl(code)}
      alt={code}
      className={cn("inline-block h-3.5 w-5 rounded-[1px] object-cover", className)}
      loading="lazy"
    />
  );
}

/** Resolve a country name (any locale) to its ISO alpha-2 code */
function nameToAlpha2(name: string): string | undefined {
  for (const locale of ["en", "fr", "de"]) {
    const code = countries.getAlpha2Code(name, locale);
    if (code) return code;
  }
  return undefined;
}

export interface CountryComboboxProps {
  /** Country value — either an ISO alpha-2 code or a country name (English). */
  value: string;
  /** Called with the English country name when the user selects a country. */
  onValueChange: (value: string) => void;
  /** Optional: called with the ISO alpha-2 code when the user selects a country. */
  onCodeChange?: (code: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

interface CountryOption {
  code: string;
  label: string;
  enName: string;
}

export function CountryCombobox({ value, onValueChange, onCodeChange, disabled, placeholder, className }: CountryComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const { i18n } = useTranslation();

  const locale = ["en", "fr", "de"].includes(i18n.language) ? i18n.language : "en";

  const countryList = React.useMemo<CountryOption[]>(() => {
    const namesObj = countries.getNames(locale, { select: "official" });
    return Object.entries(namesObj)
      .map(([code, label]) => ({
        code,
        label,
        enName: countries.getName(code, "en") ?? label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [locale]);

  // Resolve current value to a code
  const selectedCode = React.useMemo(() => {
    if (!value) return undefined;
    if (value.length === 2 && countries.getName(value.toUpperCase(), "en")) {
      return value.toUpperCase();
    }
    return nameToAlpha2(value);
  }, [value]);

  const selectedOption = countryList.find((c) => c.code === selectedCode);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !selectedOption && "text-muted-foreground", className)}
        >
          {selectedOption ? (
            <span className="flex items-center gap-2 truncate">
              <FlagIcon code={selectedOption.code} />
              {selectedOption.label}
            </span>
          ) : (
            placeholder ?? "Select country..."
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={"Search country..."} />
          <CommandList>
            <CommandEmpty>{"No country found."}</CommandEmpty>
            <CommandGroup>
              {countryList.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.label} ${c.enName}`}
                  onSelect={() => {
                    onValueChange(c.enName);
                    onCodeChange?.(c.code);
                    setOpen(false);
                  }}
                >
                  <FlagIcon code={c.code} className="mr-1" />
                  {c.label}
                  <Check className={cn("ml-auto h-4 w-4", selectedCode === c.code ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Display-only: renders a flag + country name from a country name or code */
export function CountryDisplay({ value, className }: { value: string | null | undefined; className?: string }) {
  const { i18n } = useTranslation();
  const locale = ["en", "fr", "de"].includes(i18n.language) ? i18n.language : "en";

  if (!value) return <span className={className}>-</span>;

  let code = value.length === 2 && countries.getName(value.toUpperCase(), "en") ? value.toUpperCase() : undefined;
  if (!code) code = nameToAlpha2(value);

  if (!code) return <span className={className}>{value}</span>;

  const localName = countries.getName(code, locale) ?? value;

  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <FlagIcon code={code} />
      {localName}
    </span>
  );
}
